import { Fragment, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Plus, Trash2, Pencil, Settings as SettingsIcon, Home as HomeIcon,
  ShoppingBag, X, Users as UsersIcon, ScrollText, LogOut,
  Mail, FileText, Download, ChevronDown, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import {
  fetchProducts, createProduct, updateProduct, deleteProduct, bulkUpdateProducts,
  fetchSettings, updateSettings,
  fetchUsers, setUserApproval, deleteUser,
  fetchOrders, updateOrderStatus, deleteOrder,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { testIds } from "@/constants/testIds/b2b";
import SortSelect, { applySort, cmpStr, cmpNum, cmpDate, cmpBool } from "@/components/SortSelect";
import { fmtCAD } from "@/lib/currency";
import ImagesUploader from "@/components/admin/ImagesUploader";
import CatalogUploader from "@/components/admin/CatalogUploader";
import EmailTemplatesPanel from "@/components/admin/EmailTemplatesPanel";
import HomeGalleryUploader from "@/components/admin/HomeGalleryUploader";
import PendingApplications from "@/components/admin/PendingApplications";
import BuyerAccounts from "@/components/admin/BuyerAccounts";
import TermsEditor from "@/components/admin/TermsEditor";
import OrderTrackingForm from "@/components/admin/OrderTrackingForm";
import InvoiceEditor from "@/components/admin/InvoiceEditor";

const CATEGORIES = ["Tops", "Bottoms", "Dresses", "Outerwear", "Accessories"];
const COMMON_SIZES = ["0-3M", "3-6M", "6-12M", "1-2Y", "2-3Y", "3-4Y", "4-5Y", "5-6Y", "6-7Y", "7-8Y"];
const STATUSES = ["pending", "confirmed", "invoiced", "paid", "cancelled"];

const PRODUCT_SORT_OPTIONS = [
  { value: "name_asc", label: "Name A → Z" },
  { value: "name_desc", label: "Name Z → A" },
  { value: "category_asc", label: "Category A → Z" },
  { value: "price_asc", label: "Wholesale low → high" },
  { value: "price_desc", label: "Wholesale high → low" },
];
const PRODUCT_COMPARATORS = {
  name_asc: cmpStr((p) => p.name, "asc"),
  name_desc: cmpStr((p) => p.name, "desc"),
  category_asc: cmpStr((p) => p.category, "asc"),
  price_asc: cmpNum((p) => p.wholesale_price, "asc"),
  price_desc: cmpNum((p) => p.wholesale_price, "desc"),
};

const USER_SORT_OPTIONS = [
  { value: "business_asc", label: "Business A → Z" },
  { value: "business_desc", label: "Business Z → A" },
  { value: "newest", label: "Newest first" },
  { value: "pending_first", label: "Pending first" },
];
const USER_COMPARATORS = {
  business_asc: cmpStr((u) => u.business_name || u.email, "asc"),
  business_desc: cmpStr((u) => u.business_name || u.email, "desc"),
  newest: cmpDate((u) => u.created_at, "desc"),
  pending_first: cmpBool((u) => u.role !== "admin" && !u.is_approved, true),
};

const ADMIN_ORDER_SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "business_asc", label: "Business A → Z" },
  { value: "business_desc", label: "Business Z → A" },
  { value: "total_desc", label: "Total high → low" },
  { value: "total_asc", label: "Total low → high" },
];
const ADMIN_ORDER_COMPARATORS = {
  newest: cmpDate((o) => o.created_at, "desc"),
  oldest: cmpDate((o) => o.created_at, "asc"),
  business_asc: cmpStr((o) => o.business_name || o.email, "asc"),
  business_desc: cmpStr((o) => o.business_name || o.email, "desc"),
  total_desc: cmpNum((o) => o.subtotal, "desc"),
  total_asc: cmpNum((o) => o.subtotal, "asc"),
};

const emptyForm = {
  name: "",
  description: "",
  msrp: "",
  wholesale_price: "",
  moq: 10,
  category: "Tops",
  sizes: [],
  images: [""],
  featured: false,
  sku: "",
};

export default function Admin() {
  const { user, isAdmin, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("products");
  const [usersSubTab, setUsersSubTab] = useState("pending");
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState({
    shopify_url: "https://rowenkidsclub.com",
    brand_name: "Rowen Kids Club",
    prebook_label: "Prebook · SS26",
    categories: ["Tops", "Bottoms", "Dresses", "Outerwear", "Accessories"],
    gst_number: "",
    e_transfer_instructions: "",
    invoice_footer_note: "",
  });
  const [newCategory, setNewCategory] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [productSort, setProductSort] = useState("name_asc");
  const [userSort, setUserSort] = useState("pending_first");
  const [orderSort, setOrderSort] = useState("newest");
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({
    category_enabled: false,
    category: "Tops",
    product_type_enabled: false,
    product_type: "prebook",
    featured_enabled: false,
    featured: false,
  });
  const [bulkSaving, setBulkSaving] = useState(false);

  const sortedProducts = useMemo(
    () => applySort(products, productSort, PRODUCT_COMPARATORS),
    [products, productSort],
  );
  const sortedUsers = useMemo(
    () => applySort(users, userSort, USER_COMPARATORS),
    [users, userSort],
  );
  const sortedAdminOrders = useMemo(
    () => applySort(orders, orderSort, ADMIN_ORDER_COMPARATORS),
    [orders, orderSort],
  );

  useEffect(() => {
    if (!loading && !isAdmin) {
      nav("/login", { replace: true, state: { from: "/admin" } });
    }
  }, [loading, isAdmin, nav]);

  const loadAll = async () => {
    try {
      const [p, s, u, o] = await Promise.all([
        fetchProducts(),
        fetchSettings(),
        fetchUsers().catch(() => []),
        fetchOrders().catch(() => []),
      ]);
      setProducts(p);
      setSettings((prev) => ({ ...prev, ...s }));
      setUsers(u);
      setOrders(o);
    } catch (e) {
      toast.error("Failed to load data");
    }
  };

  useEffect(() => {
    if (isAdmin) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  if (loading || !isAdmin) {
    return <div className="min-h-screen flex items-center justify-center text-foreground/70">Loading…</div>;
  }

  // ---------- Product modal ----------
  const openNew = () => { setForm(emptyForm); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      name: p.name || "",
      description: p.description || "",
      msrp: String(p.msrp ?? ""),
      wholesale_price: String(p.wholesale_price ?? ""),
      moq: p.moq || 10,
      category: p.category || "Tops",
      sizes: (p.sizes || []).map((v) =>
        typeof v === "string"
          ? { name: v, sku: "", price_override: "", quantity: "" }
          : { name: v.name || "", sku: v.sku || "", price_override: v.price_override ?? "", quantity: v.quantity ?? "" },
      ),
      images: p.images?.length ? p.images : [""],
      featured: !!p.featured,
      product_type: p.product_type || "prebook",
      delivery_window: p.delivery_window || "",
      order_by_date: p.order_by_date || "",
      sku: p.sku || "",
    });
    setEditing(p.id);
  };
  const closeForm = () => { setEditing(null); setForm(emptyForm); };

  // ---- Size variants ({ name, sku, price_override, quantity }) ----
  const addVariant = (name = "") =>
    setForm((f) => ({ ...f, sizes: [...f.sizes, { name, sku: "", price_override: "", quantity: "" }] }));
  const updateVariant = (idx, field, value) =>
    setForm((f) => ({ ...f, sizes: f.sizes.map((v, i) => (i === idx ? { ...v, [field]: value } : v)) }));
  const removeVariant = (idx) =>
    setForm((f) => ({ ...f, sizes: f.sizes.filter((_, i) => i !== idx) }));
  const quickAddSize = (name) =>
    setForm((f) =>
      f.sizes.some((v) => v.name === name)
        ? f
        : { ...f, sizes: [...f.sizes, { name, sku: "", price_override: "", quantity: "" }] },
    );

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Name is required");
    const msrp = parseFloat(form.msrp);
    const wholesale = parseFloat(form.wholesale_price);
    const moq = parseInt(form.moq, 10) || 10;
    if (isNaN(msrp) || msrp < 0) return toast.error("Enter a valid MSRP");
    if (isNaN(wholesale) || wholesale < 0) return toast.error("Enter a valid wholesale price");

    const payload = {
      name: form.name.trim(),
      description: form.description,
      msrp,
      wholesale_price: wholesale,
      moq,
      category: form.category,
      sizes: form.sizes
        .filter((v) => (v.name || "").trim())
        .map((v) => ({
          name: v.name.trim(),
          sku: (v.sku || "").trim() || null,
          price_override:
            v.price_override === "" || v.price_override === null || v.price_override === undefined
              ? null
              : parseFloat(v.price_override),
          quantity:
            form.product_type !== "in_stock" || v.quantity === "" || v.quantity === null || v.quantity === undefined
              ? null
              : parseInt(v.quantity, 10),
        })),
      images: form.images.map((i) => i.trim()).filter(Boolean),
      featured: form.featured,
      product_type: form.product_type || "prebook",
      delivery_window: form.delivery_window?.trim() || null,
      order_by_date: form.order_by_date?.trim() || null,
      sku: form.sku?.trim() || null,
    };
    setSaving(true);
    try {
      if (editing === "new") await createProduct(payload);
      else await updateProduct(editing, payload);
      toast.success("Saved");
      closeForm();
      loadAll();
    } catch (err) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this product? This can't be undone.")) return;
    try { await deleteProduct(id); toast.success("Deleted"); loadAll(); }
    catch { toast.error("Delete failed"); }
  };

  const toggleProductSelected = (id) => {
    setSelectedProductIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAllProducts = () => {
    const allIds = sortedProducts.map((p) => p.id);
    const allSelected = allIds.length > 0 && allIds.every((id) => selectedProductIds.includes(id));
    setSelectedProductIds(allSelected ? [] : allIds);
  };

  const openBulkEdit = () => {
    setBulkForm({
      category_enabled: false,
      category: "Tops",
      product_type_enabled: false,
      product_type: "prebook",
      featured_enabled: false,
      featured: false,
    });
    setBulkOpen(true);
  };

  const submitBulkEdit = async (e) => {
    e.preventDefault();
    const payload = { ids: selectedProductIds };
    if (bulkForm.category_enabled) payload.category = bulkForm.category;
    if (bulkForm.product_type_enabled) payload.product_type = bulkForm.product_type;
    if (bulkForm.featured_enabled) payload.featured = bulkForm.featured;
    if (Object.keys(payload).length === 1) {
      toast.error("Pick at least one field to update");
      return;
    }
    setBulkSaving(true);
    try {
      const res = await bulkUpdateProducts(payload);
      toast.success(`Updated ${res.modified} of ${res.matched} products`);
      setBulkOpen(false);
      setSelectedProductIds([]);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulkSaving(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    if (!(settings.categories || []).length) {
      toast.error("Add at least one product category before saving.");
      return;
    }
    try {
      const saved = await updateSettings({
        shopify_url: settings.shopify_url,
        brand_name: settings.brand_name,
        prebook_label: settings.prebook_label,
        categories: settings.categories,
        gst_number: settings.gst_number,
        e_transfer_instructions: settings.e_transfer_instructions,
        invoice_footer_note: settings.invoice_footer_note,
      });
      setSettings((prev) => ({ ...prev, ...saved }));
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
  };

  const toggleApproval = async (u) => {
    try {
      const updated = await setUserApproval(u.id, !u.is_approved);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? updated : x)));
      toast.success(updated.is_approved ? "Buyer approved" : "Approval revoked");
    } catch { toast.error("Could not update"); }
  };

  const declineApplication = async (u) => {
    if (!window.confirm(`Decline ${u.business_name || u.email}'s application? This removes their pending record.`)) return;
    try {
      await deleteUser(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
      toast.success("Application declined");
    } catch { toast.error("Could not decline"); }
  };

  const pendingApplications = users.filter((u) => u.role !== "admin" && !u.is_approved);
  const approvedBuyers = users.filter((u) => u.role !== "admin" && u.is_approved);

  const changeOrderStatus = async (o, status) => {
    try {
      const updated = await updateOrderStatus(o.id, status);
      setOrders((prev) => prev.map((x) => (x.id === o.id ? updated : x)));
      toast.success(`Order ${status}`);
    } catch { toast.error("Could not update"); }
  };

  const orderDisplayNumber = (o) =>
    o.order_number || `#${(o.id || "").slice(0, 8).toUpperCase()}`;

  const handleDeleteOrder = async (o) => {
    const ref = orderDisplayNumber(o);
    const confirmed = window.confirm(
      `⚠️ ADMIN ACTION — DELETE ORDER ${ref}\n\n` +
      `This will permanently remove the order, its line items, and any invoice/payment record from the database.\n\n` +
      `The buyer will NOT be notified. This cannot be undone.\n\n` +
      `Type OK to confirm.`
    );
    if (!confirmed) return;
    try {
      await deleteOrder(o.id);
      setOrders((prev) => prev.filter((x) => x.id !== o.id));
      toast.success(`Order ${ref} deleted`);
    } catch {
      toast.error("Could not delete order");
    }
  };

  const downloadOrdersCsv = (fulfillmentType, filename) => {
    const matching = sortedAdminOrders.filter(
      (o) => (o.fulfillment_type || "prebook") === fulfillmentType,
    );
    if (matching.length === 0) {
      toast.error(`No ${fulfillmentType === "in_stock" ? "in-stock" : "prebook"} orders to export`);
      return;
    }
    const headers = [
      "Order #", "Created", "Store name", "Contact", "Email", "Province",
      "Fulfillment", "Payment terms", "Status",
      "Product", "SKU", "Size", "Qty", "Unit price", "Line total",
      "Subtotal", "GST", "PST", "Tax paid", "Total", "Due date",
      "Shipping address", "Notes",
    ];
    const escape = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const rows = [headers.join(",")];
    matching.forEach((o) => {
      const taxPaid = (Number(o.gst) || 0) + (Number(o.pst) || 0);
      (o.items || []).forEach((it) => {
        rows.push([
          orderDisplayNumber(o),
          o.created_at || "",
          o.business_name || "",
          o.contact_name || "",
          o.email || "",
          o.province || "",
          o.fulfillment_type || "",
          o.payment_terms || "",
          o.status || "",
          it.name || "",
          it.sku || "",
          it.size || "",
          it.quantity ?? "",
          (Number(it.unit_wholesale_price) || 0).toFixed(2),
          (Number(it.line_total) || 0).toFixed(2),
          (Number(o.subtotal) || 0).toFixed(2),
          (Number(o.gst) || 0).toFixed(2),
          (Number(o.pst) || 0).toFixed(2),
          taxPaid.toFixed(2),
          (Number(o.total) || 0).toFixed(2),
          o.due_date || "",
          o.shipping_address || "",
          o.notes || "",
        ].map(escape).join(","));
      });
    });
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `${filename}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${matching.length} ${fulfillmentType === "in_stock" ? "in-stock" : "prebook"} order${matching.length === 1 ? "" : "s"}`);
  };

  const productCategories = settings.categories?.length ? settings.categories : CATEGORIES;

  const addCategory = () => {
    const name = newCategory.trim();
    if (!name) return;
    if ((settings.categories || []).some((c) => c.toLowerCase() === name.toLowerCase())) {
      toast.error("That category already exists");
      return;
    }
    setSettings((s) => ({ ...s, categories: [...(s.categories || []), name] }));
    setNewCategory("");
  };

  const removeCategory = (name) =>
    setSettings((s) => ({ ...s, categories: (s.categories || []).filter((c) => c !== name) }));

  return (
    <div data-testid="admin-page" className="page-card flex flex-col md:flex-row">
      {/* Sidebar */}
      <aside className="md:w-64 md:min-h-screen border-b md:border-b-0 md:border-r border-border bg-[hsl(var(--surface))]/60 p-6 md:p-8 flex md:flex-col gap-4 md:gap-2">
        <Link to="/" className="font-serif text-2xl mb-0 md:mb-10 flex-1 md:flex-none" data-testid="admin-logo">
          Rowen <span className="italic">Admin</span>
        </Link>
        <nav className="flex md:flex-col gap-1 md:gap-1 overflow-x-auto">
          <TabBtn id="products" tab={tab} setTab={setTab} icon={ShoppingBag} label="Products" testid={testIds.adminTabProducts} />
          <TabBtn id="users" tab={tab} setTab={setTab} icon={UsersIcon} label="Buyers" testid={testIds.adminTabUsers} />
          <TabBtn id="orders" tab={tab} setTab={setTab} icon={ScrollText} label="Orders" testid={testIds.adminTabOrders} />
          <TabBtn id="emails" tab={tab} setTab={setTab} icon={Mail} label="Emails" testid="admin-tab-emails" />
          <TabBtn id="terms" tab={tab} setTab={setTab} icon={FileText} label="Terms" testid={testIds.adminTabTerms} />
          <TabBtn id="settings" tab={tab} setTab={setTab} icon={SettingsIcon} label="Settings" testid={testIds.adminTabSettings} />
        </nav>
        <div className="hidden md:flex md:flex-col gap-2 mt-auto">
          <Link to="/" data-testid="admin-back-to-store" className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">
            <HomeIcon size={14} strokeWidth={1.5} /> Back to store
          </Link>
          <button onClick={async () => { await logout(); nav("/"); }} data-testid="admin-logout" className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground">
            <LogOut size={14} strokeWidth={1.5} /> Log out
          </button>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-12 max-w-6xl">
        {pendingApplications.length > 0 && tab !== "users" && (
          <button
            type="button"
            onClick={() => { setTab("users"); setUsersSubTab("pending"); }}
            data-testid="pending-applications-banner"
            className="w-full mb-8 flex items-center justify-between gap-3 px-5 py-3 rounded-2xl border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-colors text-left"
          >
            <span className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center min-w-[28px] h-7 px-2 rounded-full bg-amber-500 text-white text-xs font-semibold">
                {pendingApplications.length}
              </span>
              <span className="text-sm">
                <strong className="font-medium">
                  {pendingApplications.length === 1 ? "New application" : "New applications"} waiting
                </strong>{" "}
                · review and approve to unlock wholesale access
              </span>
            </span>
            <span className="text-xs uppercase tracking-[0.18em]">Review →</span>
          </button>
        )}

        {tab === "products" && (
          <section>
            <div className="flex items-center justify-between mb-10">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
                  {products.length} {products.length === 1 ? "item" : "items"}
                </p>
                <h1 className="font-serif text-4xl md:text-5xl">Products</h1>
              </div>
              <div className="flex items-center gap-3">
                <SortSelect
                  value={productSort}
                  onChange={setProductSort}
                  options={PRODUCT_SORT_OPTIONS}
                  testid="admin-products-sort"
                />
                <button
                  data-testid="admin-add-product-btn"
                  onClick={openNew}
                  className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-primary text-primary-foreground hover:-translate-y-0.5 transition-transform shadow-soft"
                >
                  <Plus size={16} strokeWidth={1.5} /> Add product
                </button>
              </div>
            </div>

            {products.length === 0 ? (
              <Empty title="No products yet" body='Click "Add product" to create your first piece.' testid="admin-products-empty" />
            ) : (
              <>
                {selectedProductIds.length > 0 && (
                  <div
                    data-testid="admin-bulk-actions-bar"
                    className="mb-4 flex flex-wrap items-center justify-between gap-3 px-5 py-3 rounded-2xl border border-primary/40 bg-primary/5"
                  >
                    <p className="text-sm">
                      <span className="font-medium" data-testid="admin-bulk-selected-count">
                        {selectedProductIds.length}
                      </span>{" "}
                      product{selectedProductIds.length === 1 ? "" : "s"} selected
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        data-testid="admin-bulk-edit-btn"
                        onClick={openBulkEdit}
                        className="px-4 py-2 rounded-full bg-primary text-primary-foreground text-sm hover:-translate-y-0.5 transition-transform shadow-soft"
                      >
                        Bulk edit
                      </button>
                      <button
                        type="button"
                        data-testid="admin-bulk-clear-btn"
                        onClick={() => setSelectedProductIds([])}
                        className="px-4 py-2 rounded-full border border-border text-sm hover:bg-foreground/5"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                )}
                <div data-testid="admin-products-table" className="rounded-2xl border border-border overflow-x-auto">
                <table className="w-full text-sm min-w-[760px]">
                  <thead className="bg-[hsl(var(--surface))]">
                    <tr className="text-left text-xs uppercase tracking-[0.15em] text-muted-foreground">
                      <th className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          data-testid="admin-products-select-all"
                          aria-label="Select all products"
                          className="w-4 h-4 accent-[hsl(var(--primary))] cursor-pointer"
                          checked={
                            sortedProducts.length > 0 &&
                            sortedProducts.every((p) => selectedProductIds.includes(p.id))
                          }
                          onChange={toggleSelectAllProducts}
                        />
                      </th>
                      <th className="px-4 py-3"></th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">SKU</th>
                      <th className="px-4 py-3">Category</th>
                      <th className="px-4 py-3">Wholesale</th>
                      <th className="px-4 py-3">MSRP</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3">Featured</th>
                      <th className="px-4 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedProducts.map((p) => (
                      <tr key={p.id} className="border-t border-border" data-testid={`admin-row-${p.id}`}>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            data-testid={`admin-row-select-${p.id}`}
                            aria-label={`Select ${p.name}`}
                            className="w-4 h-4 accent-[hsl(var(--primary))] cursor-pointer"
                            checked={selectedProductIds.includes(p.id)}
                            onChange={() => toggleProductSelected(p.id)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <Link
                            to={`/product/${p.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`admin-product-thumb-link-${p.id}`}
                            className="block w-12 h-14 rounded-lg overflow-hidden bg-[hsl(var(--surface))]"
                          >
                            {p.images?.[0] && <img src={p.images[0]} alt={p.name} className="w-full h-full object-cover" />}
                          </Link>
                        </td>
                        <td className="px-4 py-3 font-serif text-base">
                          <Link
                            to={`/product/${p.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`admin-product-name-link-${p.id}`}
                            className="hover:underline decoration-1 underline-offset-2 hover:text-primary transition-colors"
                          >
                            {p.name}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-foreground/70 text-xs">{p.sku || "—"}</td>
                        <td className="px-4 py-3 text-foreground/70">{p.category}</td>
                        <td className="px-4 py-3">{fmtCAD(p.wholesale_price ?? 0)}</td>
                        <td className="px-4 py-3">{fmtCAD(p.msrp ?? 0)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-[0.1em] border ${
                            p.product_type === "in_stock"
                              ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                              : "bg-sky-50 border-sky-200 text-sky-700"
                          }`}>
                            {p.product_type === "in_stock" ? "In stock" : "Prebook"}
                          </span>
                        </td>
                        <td className="px-4 py-3">{p.featured ? "Yes" : "—"}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex gap-1">
                            <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="p-2 rounded-full hover:bg-foreground/5" aria-label="Edit">
                              <Pencil size={15} strokeWidth={1.5} />
                            </button>
                            <button data-testid={`admin-delete-${p.id}`} onClick={() => handleDelete(p.id)} className="p-2 rounded-full hover:bg-destructive/10 text-destructive" aria-label="Delete">
                              <Trash2 size={15} strokeWidth={1.5} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </>
            )}
          </section>
        )}

        {tab === "users" && (
          <section>
            <div className="mb-8">
              <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Buyers</p>
              <h1 className="font-serif text-4xl md:text-5xl">Accounts</h1>
            </div>

            <div className="inline-flex items-center gap-1 p-1 rounded-full border border-border bg-background mb-8" data-testid="users-subtab-group">
              <SubTabBtn
                id="pending"
                current={usersSubTab}
                onClick={setUsersSubTab}
                count={pendingApplications.length}
                accent={pendingApplications.length > 0}
              >
                Pending accounts
              </SubTabBtn>
              <SubTabBtn
                id="approved"
                current={usersSubTab}
                onClick={setUsersSubTab}
                count={approvedBuyers.length}
              >
                Buyer accounts
              </SubTabBtn>
            </div>

            {usersSubTab === "pending" && (
              pendingApplications.length === 0 ? (
                <Empty title="No pending applications" body="When a store applies for wholesale access, their full application appears here for review." testid="pending-empty" />
              ) : (
                <PendingApplications
                  pending={pendingApplications}
                  onApprove={toggleApproval}
                  onReject={declineApplication}
                />
              )
            )}

            {usersSubTab === "approved" && (
              <BuyerAccounts
                buyers={approvedBuyers}
                orders={orders}
                onRefresh={loadAll}
              />
            )}
          </section>
        )}

        {tab === "orders" && (
          <section>
            <div className="flex items-center justify-between mb-10 flex-wrap gap-4">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Invoice ledger</p>
                <h1 className="font-serif text-4xl md:text-5xl">Orders</h1>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {orders.length > 0 && (
                  <>
                    <button
                      type="button"
                      data-testid="admin-orders-csv-in-stock"
                      onClick={() => downloadOrdersCsv("in_stock", "rowen-orders-in-stock")}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-xs uppercase tracking-[0.14em] hover:bg-foreground/5"
                    >
                      <Download size={14} strokeWidth={1.5} /> In-Stock CSV
                    </button>
                    <button
                      type="button"
                      data-testid="admin-orders-csv-prebook"
                      onClick={() => downloadOrdersCsv("prebook", "rowen-orders-prebook")}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-border text-xs uppercase tracking-[0.14em] hover:bg-foreground/5"
                    >
                      <Download size={14} strokeWidth={1.5} /> Prebook CSV
                    </button>
                    <SortSelect
                      value={orderSort}
                      onChange={setOrderSort}
                      options={ADMIN_ORDER_SORT_OPTIONS}
                      testid="admin-orders-sort"
                    />
                  </>
                )}
              </div>
            </div>
            {orders.length === 0 ? (
              <Empty title="No orders yet" body="Approved buyers' orders will show up here." testid="admin-orders-empty" />
            ) : (
              <div data-testid="admin-orders-table" className="rounded-2xl border border-border overflow-x-auto">
                <table className="w-full text-sm min-w-[860px]">
                  <thead className="bg-[hsl(var(--surface))]">
                    <tr className="text-left text-xs uppercase tracking-[0.15em] text-muted-foreground">
                      <th className="px-3 py-3 w-8"></th>
                      <th className="px-3 py-3">Order #</th>
                      <th className="px-3 py-3">Store name</th>
                      <th className="px-3 py-3">Type</th>
                      <th className="px-3 py-3">Terms</th>
                      <th className="px-3 py-3">Created</th>
                      <th className="px-3 py-3 text-right">Total</th>
                      <th className="px-3 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedAdminOrders.map((o) => {
                      const expanded = expandedOrderId === o.id;
                      const taxPaid = (Number(o.gst) || 0) + (Number(o.pst) || 0);
                      const totalAmt = Number(o.total) || Number(o.subtotal) || 0;
                      return (
                        <Fragment key={o.id}>
                          <tr
                            data-testid={`admin-order-${o.id}`}
                            className="border-t border-border hover:bg-foreground/5 cursor-pointer"
                            onClick={() => setExpandedOrderId(expanded ? null : o.id)}
                          >
                            <td className="px-3 py-3 align-top">
                              <button
                                type="button"
                                data-testid={`admin-order-expand-${o.id}`}
                                onClick={(e) => { e.stopPropagation(); setExpandedOrderId(expanded ? null : o.id); }}
                                className="p-1 rounded-full hover:bg-foreground/10"
                                aria-label={expanded ? "Collapse" : "Expand"}
                              >
                                {expanded
                                  ? <ChevronDown size={16} strokeWidth={1.5} />
                                  : <ChevronRight size={16} strokeWidth={1.5} />}
                              </button>
                            </td>
                            <td className="px-3 py-3 font-mono text-xs align-top" data-testid={`admin-order-number-${o.id}`}>
                              {orderDisplayNumber(o)}
                            </td>
                            <td className="px-3 py-3 align-top" data-testid={`admin-order-store-${o.id}`}>
                              <div className="font-serif text-base">{o.business_name || "—"}</div>
                              <div className="text-xs text-muted-foreground">{o.contact_name}</div>
                              <div className="text-xs text-muted-foreground">{o.email}</div>
                            </td>
                            <td className="px-3 py-3 align-top">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] uppercase tracking-[0.1em] border ${
                                (o.fulfillment_type || "prebook") === "in_stock"
                                  ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                                  : "bg-sky-50 border-sky-200 text-sky-700"
                              }`}>
                                {(o.fulfillment_type || "prebook") === "in_stock" ? "In stock" : "Prebook"}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-xs text-muted-foreground align-top">
                              {o.payment_terms || "—"}
                            </td>
                            <td className="px-3 py-3 text-xs text-muted-foreground align-top">
                              {new Date(o.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-3 py-3 font-serif text-base text-right align-top">
                              {fmtCAD(totalAmt)}
                            </td>
                            <td className="px-3 py-3 align-top" onClick={(e) => e.stopPropagation()}>
                              <select
                                data-testid={`${testIds.adminOrderStatus}-${o.id}`}
                                value={o.status}
                                onChange={(e) => changeOrderStatus(o, e.target.value)}
                                className="px-3 py-1.5 rounded-full border border-border bg-transparent text-xs uppercase tracking-[0.15em] focus:outline-none focus:border-primary"
                              >
                                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </td>
                          </tr>
                          {expanded && (
                            <tr className="border-t border-border bg-[hsl(var(--surface))]/40">
                              <td></td>
                              <td colSpan={7} className="px-3 py-4">
                                <div data-testid={`admin-order-detail-${o.id}`} className="space-y-4">
                                  <div className="flex items-center justify-end">
                                    <a
                                      href={`/invoice/${o.id}`}
                                      target="_blank"
                                      rel="noreferrer"
                                      data-testid={`admin-invoice-print-${o.id}`}
                                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-xs uppercase tracking-wider border border-border hover:bg-secondary"
                                    >
                                      <Download size={12} strokeWidth={1.75} /> Print / Download invoice
                                    </a>
                                  </div>
                                  <InvoiceEditor
                                    order={o}
                                    onSaved={(u) => setOrders((prev) => prev.map((x) => (x.id === u.id ? u : x)))}
                                  />

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    <div className="space-y-1">
                                      {o.shipping_address && (
                                        <p><span className="text-muted-foreground">Ship to:</span> {o.shipping_address}</p>
                                      )}
                                      {o.notes && (
                                        <p><span className="text-muted-foreground">Notes:</span> {o.notes}</p>
                                      )}
                                      {o.due_date && (
                                        <p><span className="text-muted-foreground">Due:</span> {o.due_date}</p>
                                      )}
                                      <p><span className="text-muted-foreground">Province:</span> {o.province || "—"}</p>
                                    </div>
                                  </div>
                                  <div className="mt-4">
                                    <OrderTrackingForm
                                      order={o}
                                      onSaved={(u) => setOrders((prev) => prev.map((x) => (x.id === u.id ? u : x)))}
                                    />
                                  </div>
                                  <div className="mt-6 rounded border border-destructive/40 bg-destructive/5 p-4">
                                    <div className="flex items-start gap-3">
                                      <Trash2 size={18} strokeWidth={1.6} className="text-destructive shrink-0 mt-0.5" />
                                      <div className="flex-1">
                                        <p className="text-sm font-semibold text-destructive uppercase tracking-wider">Danger zone — admin only</p>
                                        <p className="text-xs text-muted-foreground mt-1">
                                          Permanently delete this order. The buyer is not notified. This cannot be undone — use only for test orders or data cleanup.
                                        </p>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleDeleteOrder(o)}
                                        data-testid={`admin-delete-order-${o.id}`}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-xs uppercase tracking-wider font-medium border border-destructive text-destructive hover:bg-destructive hover:text-destructive-foreground transition-colors shrink-0"
                                      >
                                        <Trash2 size={13} strokeWidth={1.75} /> Delete order
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {tab === "emails" && (
          <section data-testid="admin-emails-section">
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Transactional</p>
            <h1 className="font-serif text-4xl md:text-5xl mb-10">Emails</h1>
            <EmailTemplatesPanel adminEmail={user?.email} />
          </section>
        )}

        {tab === "terms" && (
          <section>
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Wholesale</p>
            <h1 className="font-serif text-4xl md:text-5xl mb-3">Global terms</h1>
            <p className="text-sm text-muted-foreground mb-10 max-w-2xl">
              Edit the terms shown to buyers at checkout. Buyers see a soft warning if they haven&apos;t accepted the latest version. Toggle &quot;Force buyers to re-sign&quot; to bump the version and require everyone to re-accept.
            </p>
            <TermsEditor />
          </section>
        )}

        {tab === "settings" && (
          <section>
            <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Configure</p>
            <h1 className="font-serif text-4xl md:text-5xl mb-10">Settings</h1>

            <form onSubmit={handleSaveSettings} className="space-y-6 max-w-xl" data-testid="admin-settings-form">
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">Brand name</label>
                <input
                  data-testid="settings-brand-name"
                  type="text"
                  value={settings.brand_name}
                  onChange={(e) => setSettings({ ...settings, brand_name: e.target.value })}
                  className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">Prebook buy name</label>
                <input
                  data-testid="settings-prebook-label"
                  type="text"
                  placeholder="Prebook · SS26"
                  value={settings.prebook_label || ""}
                  onChange={(e) => setSettings({ ...settings, prebook_label: e.target.value })}
                  className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Shown on the shop tab/heading and the prebook lookbook label.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">Product categories</label>
                <div className="flex flex-wrap gap-2 mb-3" data-testid="settings-categories-list">
                  {(settings.categories || []).map((c) => (
                    <span
                      key={c}
                      data-testid={`settings-category-${c}`}
                      className="inline-flex items-center gap-1.5 pl-4 pr-2 py-1.5 rounded-full border border-border bg-background text-sm"
                    >
                      {c}
                      <button
                        type="button"
                        data-testid={`settings-category-remove-${c}`}
                        onClick={() => removeCategory(c)}
                        aria-label={`Remove ${c}`}
                        className="p-1 rounded-full hover:bg-destructive/10 text-destructive"
                      >
                        <X size={13} strokeWidth={1.75} />
                      </button>
                    </span>
                  ))}
                  {(settings.categories || []).length === 0 && (
                    <p className="text-sm text-muted-foreground">No categories yet — add one below.</p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    data-testid="settings-category-input"
                    type="text"
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCategory(); } }}
                    placeholder="Add a category (e.g. Swim)"
                    className="flex-1 px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary"
                  />
                  <button
                    type="button"
                    data-testid="settings-category-add"
                    onClick={addCategory}
                    className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full border border-border text-sm hover:bg-foreground/5 shrink-0"
                  >
                    <Plus size={14} strokeWidth={1.5} /> Add
                  </button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Used in the product form, shop filters, and bulk edit. Click &quot;Save settings&quot; to apply.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">Retail / Shopify URL (footer &quot;Shop Retail&quot; link)</label>
                <input
                  data-testid="settings-shopify-url"
                  type="url"
                  placeholder="https://rowenkidsclub.com"
                  value={settings.shopify_url}
                  onChange={(e) => setSettings({ ...settings, shopify_url: e.target.value })}
                  className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">GST number</label>
                <input
                  data-testid="settings-gst-number"
                  type="text"
                  placeholder="123456789 RT0001"
                  value={settings.gst_number || ""}
                  onChange={(e) => setSettings({ ...settings, gst_number: e.target.value })}
                  className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Printed on every invoice.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">E-transfer instructions</label>
                <textarea
                  data-testid="settings-e-transfer-instructions"
                  rows={4}
                  placeholder="Send Interac e-Transfer to billing@rowenkidsclub.com. Auto-deposit enabled — no security question required."
                  value={settings.e_transfer_instructions || ""}
                  onChange={(e) => setSettings({ ...settings, e_transfer_instructions: e.target.value })}
                  className="w-full px-5 py-3 rounded-2xl border border-border bg-transparent focus:outline-none focus:border-primary"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Shown to buyers at checkout and on the invoice.</p>
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground block mb-2">Invoice footer note</label>
                <textarea
                  data-testid="settings-invoice-footer-note"
                  rows={3}
                  placeholder="Thank you for stocking Rowen Kids Club. All sales final. Questions? hello@rowenkidsclub.com"
                  value={settings.invoice_footer_note || ""}
                  onChange={(e) => setSettings({ ...settings, invoice_footer_note: e.target.value })}
                  className="w-full px-5 py-3 rounded-2xl border border-border bg-transparent focus:outline-none focus:border-primary"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Appears at the bottom of every invoice.</p>
              </div>
              <button
                data-testid="settings-save-btn"
                type="submit"
                className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-primary text-primary-foreground hover:-translate-y-0.5 transition-transform shadow-soft"
              >
                Save settings
              </button>
            </form>

            <div className="mt-14 max-w-xl">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Lookbook PDFs</p>
              <CatalogUploader />
            </div>

            <div className="mt-14 max-w-4xl">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">Homepage gallery</p>
              <p className="text-xs text-muted-foreground mb-3">Drop in editorial photos. These appear on the public landing page below the hero.</p>
              <HomeGalleryUploader />
            </div>
          </section>
        )}
      </main>

      {editing && (
        <div data-testid="admin-product-form-modal" className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-stretch md:items-center md:justify-center md:p-6">
          <div className="bg-background w-full md:max-w-2xl md:rounded-3xl overflow-y-auto max-h-screen md:max-h-[90vh]">
            <div className="sticky top-0 bg-background border-b border-border flex items-center justify-between px-6 py-4 z-10">
              <h2 className="font-serif text-2xl">{editing === "new" ? "Add product" : "Edit product"}</h2>
              <button data-testid="admin-form-close" onClick={closeForm} className="p-2 rounded-full hover:bg-foreground/5" aria-label="Close">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-5" data-testid="admin-product-form">
              <Field label="Name">
                <input data-testid="form-name" type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
                  className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary" />
              </Field>

              <Field label="Description">
                <textarea data-testid="form-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full px-5 py-3 rounded-2xl border border-border bg-transparent focus:outline-none focus:border-primary" />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Wholesale (CAD)">
                  <input data-testid="form-wholesale" type="number" min="0" step="0.01" value={form.wholesale_price} onChange={(e) => setForm({ ...form, wholesale_price: e.target.value })} required
                    className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary" />
                </Field>
                <Field label="MSRP (CAD)">
                  <input data-testid="form-msrp" type="number" min="0" step="0.01" value={form.msrp} onChange={(e) => setForm({ ...form, msrp: e.target.value })} required
                    className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary" />
                </Field>
              </div>

              <Field label="Category">
                <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary">
                  {productCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>

              <Field label="SKU (optional, base/parent reference)">
                <input
                  data-testid="form-sku"
                  type="text"
                  placeholder="e.g. RKC-AURORA-TEE"
                  value={form.sku}
                  onChange={(e) => setForm({ ...form, sku: e.target.value })}
                  className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary"
                />
              </Field>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Field label="Type">
                  <select
                    data-testid="form-product-type"
                    value={form.product_type}
                    onChange={(e) => setForm({ ...form, product_type: e.target.value })}
                    className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary"
                  >
                    <option value="prebook">Prebook</option>
                    <option value="in_stock">In Stock</option>
                  </select>
                </Field>
                <Field label="Delivery window (prebook)" hint="Ships between these two months">
                  <DeliveryWindowRange
                    value={form.delivery_window}
                    disabled={form.product_type !== "prebook"}
                    onChange={(next) => setForm({ ...form, delivery_window: next })}
                  />
                </Field>
                <Field label="Order by (prebook)">
                  <input
                    data-testid="form-order-by-date"
                    type="text"
                    placeholder="e.g. Jan 15, 2026"
                    value={form.order_by_date}
                    onChange={(e) => setForm({ ...form, order_by_date: e.target.value })}
                    disabled={form.product_type !== "prebook"}
                    className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary disabled:opacity-40"
                  />
                </Field>
              </div>

              <Field label="Sizes & variants">
                <p className="text-xs text-muted-foreground -mt-1 mb-3">
                  Add a row per size. Each gets its own SKU and an optional price override (leave blank to use the default wholesale price).
                </p>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {COMMON_SIZES.map((s) => {
                    const exists = form.sizes.some((v) => v.name === s);
                    return (
                      <button
                        key={s}
                        type="button"
                        data-testid={`form-size-quickadd-${s}`}
                        onClick={() => quickAddSize(s)}
                        disabled={exists}
                        className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                          exists
                            ? "bg-primary/10 text-primary border-primary/30 cursor-default"
                            : "border-border text-foreground/70 hover:border-foreground/40"
                        }`}
                      >
                        + {s}
                      </button>
                    );
                  })}
                </div>

                {form.sizes.length > 0 && (
                  <div className="space-y-2" data-testid="form-variants">
                    <div className={`hidden sm:grid ${form.product_type === "in_stock" ? "grid-cols-[1fr_1fr_110px_90px_36px]" : "grid-cols-[1fr_1fr_120px_36px]"} gap-2 px-1 text-[11px] uppercase tracking-[0.12em] text-muted-foreground`}>
                      <span>Size</span>
                      <span>SKU</span>
                      <span>Price override</span>
                      {form.product_type === "in_stock" && <span>Qty</span>}
                      <span />
                    </div>
                    {form.sizes.map((v, idx) => (
                      <div
                        key={idx}
                        data-testid={`form-variant-row-${idx}`}
                        className={`grid grid-cols-2 gap-2 items-center ${form.product_type === "in_stock" ? "sm:grid-cols-[1fr_1fr_110px_90px_36px]" : "sm:grid-cols-[1fr_1fr_120px_36px]"}`}
                      >
                        <input
                          data-testid={`form-variant-name-${idx}`}
                          type="text"
                          placeholder="Size (e.g. 2-3Y)"
                          value={v.name}
                          onChange={(e) => updateVariant(idx, "name", e.target.value)}
                          className="px-4 py-2.5 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary text-sm"
                        />
                        <input
                          data-testid={`form-variant-sku-${idx}`}
                          type="text"
                          placeholder="SKU"
                          value={v.sku}
                          onChange={(e) => updateVariant(idx, "sku", e.target.value)}
                          className="px-4 py-2.5 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary text-sm font-mono"
                        />
                        <input
                          data-testid={`form-variant-price-${idx}`}
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="Default"
                          value={v.price_override}
                          onChange={(e) => updateVariant(idx, "price_override", e.target.value)}
                          className="px-4 py-2.5 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary text-sm tabular-nums"
                        />
                        {form.product_type === "in_stock" && (
                          <input
                            data-testid={`form-variant-qty-${idx}`}
                            type="number"
                            min="0"
                            step="1"
                            placeholder="Qty"
                            value={v.quantity}
                            onChange={(e) => updateVariant(idx, "quantity", e.target.value)}
                            className="px-4 py-2.5 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary text-sm tabular-nums"
                          />
                        )}
                        <button
                          type="button"
                          data-testid={`form-variant-remove-${idx}`}
                          onClick={() => removeVariant(idx)}
                          aria-label="Remove size"
                          className="p-2 rounded-full hover:bg-destructive/10 text-destructive justify-self-end"
                        >
                          <Trash2 size={15} strokeWidth={1.5} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {form.product_type === "in_stock" && (
                  <p className="text-[11px] text-muted-foreground mt-2">
                    Set stock per size. Leave Qty blank for untracked (always orderable). Buyers can&apos;t order more than the available quantity.
                  </p>
                )}

                <button
                  type="button"
                  data-testid="form-variant-add"
                  onClick={() => addVariant("")}
                  className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-border text-sm hover:bg-foreground/5"
                >
                  <Plus size={14} strokeWidth={1.5} /> Add size
                </button>
              </Field>

              <Field label="Images">
                <ImagesUploader
                  images={form.images.filter(Boolean)}
                  onChange={(arr) => setForm({ ...form, images: arr.length ? arr : [""] })}
                />
              </Field>

              <label className="flex items-center gap-3 cursor-pointer">
                <input data-testid="form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="w-4 h-4 accent-[hsl(var(--primary))]" />
                <span className="text-sm">Feature on homepage</span>
              </label>

              <div className="flex gap-3 pt-2">
                <button type="button" onClick={closeForm} data-testid="form-cancel" className="px-6 py-3 rounded-full border border-border text-foreground/80 hover:bg-foreground/5">Cancel</button>
                <button type="submit" disabled={saving} data-testid="form-submit" className="px-6 py-3 rounded-full bg-primary text-primary-foreground disabled:opacity-60 hover:-translate-y-0.5 transition-transform shadow-soft">
                  {saving ? "Saving..." : editing === "new" ? "Create product" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="admin-bulk-edit-modal">
          <div className="bg-card w-full sm:max-w-md sm:rounded-3xl rounded-t-3xl shadow-2xl p-8 max-h-[90vh] overflow-y-auto">
            <div className="mb-6">
              <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">
                {selectedProductIds.length} product{selectedProductIds.length === 1 ? "" : "s"} selected
              </p>
              <h2 className="font-serif text-2xl">Bulk edit</h2>
              <p className="text-sm text-muted-foreground mt-2">
                Tick a field to apply that value to every selected product.
              </p>
            </div>

            <form onSubmit={submitBulkEdit} className="space-y-5">
              <div className="rounded-2xl border border-border p-4">
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    data-testid="bulk-category-enable"
                    checked={bulkForm.category_enabled}
                    onChange={(e) => setBulkForm({ ...bulkForm, category_enabled: e.target.checked })}
                    className="w-4 h-4 accent-[hsl(var(--primary))]"
                  />
                  <span className="text-sm font-medium">Category</span>
                </label>
                <select
                  data-testid="bulk-category"
                  disabled={!bulkForm.category_enabled}
                  value={bulkForm.category}
                  onChange={(e) => setBulkForm({ ...bulkForm, category: e.target.value })}
                  className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary disabled:opacity-40"
                >
                  {productCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div className="rounded-2xl border border-border p-4">
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    data-testid="bulk-type-enable"
                    checked={bulkForm.product_type_enabled}
                    onChange={(e) => setBulkForm({ ...bulkForm, product_type_enabled: e.target.checked })}
                    className="w-4 h-4 accent-[hsl(var(--primary))]"
                  />
                  <span className="text-sm font-medium">Product type</span>
                </label>
                <select
                  data-testid="bulk-type"
                  disabled={!bulkForm.product_type_enabled}
                  value={bulkForm.product_type}
                  onChange={(e) => setBulkForm({ ...bulkForm, product_type: e.target.value })}
                  className="w-full px-5 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary disabled:opacity-40"
                >
                  <option value="prebook">Prebook</option>
                  <option value="in_stock">In Stock</option>
                </select>
              </div>

              <div className="rounded-2xl border border-border p-4">
                <label className="flex items-center gap-3 cursor-pointer mb-3">
                  <input
                    type="checkbox"
                    data-testid="bulk-featured-enable"
                    checked={bulkForm.featured_enabled}
                    onChange={(e) => setBulkForm({ ...bulkForm, featured_enabled: e.target.checked })}
                    className="w-4 h-4 accent-[hsl(var(--primary))]"
                  />
                  <span className="text-sm font-medium">Featured on homepage</span>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="bulk-featured-value"
                    disabled={!bulkForm.featured_enabled}
                    checked={bulkForm.featured}
                    onChange={(e) => setBulkForm({ ...bulkForm, featured: e.target.checked })}
                    className="w-4 h-4 accent-[hsl(var(--primary))] disabled:opacity-40"
                  />
                  <span className="text-sm">Set as featured</span>
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setBulkOpen(false)}
                  data-testid="bulk-cancel"
                  className="px-6 py-3 rounded-full border border-border text-foreground/80 hover:bg-foreground/5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={bulkSaving}
                  data-testid="bulk-submit"
                  className="px-6 py-3 rounded-full bg-primary text-primary-foreground disabled:opacity-60 hover:-translate-y-0.5 transition-transform shadow-soft"
                >
                  {bulkSaving ? "Applying..." : "Apply to selected"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function SubTabBtn({ id, current, onClick, count, accent, children }) {
  const active = current === id;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      data-testid={`users-subtab-${id}`}
      className={`px-4 py-2 rounded-full text-xs uppercase tracking-[0.14em] transition-colors flex items-center gap-2 ${
        active
          ? "bg-foreground text-background"
          : "text-foreground/70 hover:bg-foreground/5"
      }`}
    >
      {children}
      {count !== undefined && (
        <span className={`min-w-[20px] px-1.5 py-0.5 rounded-full text-[10px] ${
          active ? "bg-background/20 text-background" : accent ? "bg-amber-500 text-white" : "bg-foreground/10 text-foreground/70"
        }`}>{count}</span>
      )}
    </button>
  );
}

function TabBtn({ id, tab, setTab, icon: Icon, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={() => setTab(id)}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm transition-colors whitespace-nowrap ${
        tab === id ? "bg-primary text-primary-foreground" : "text-foreground/70 hover:bg-foreground/5"
      }`}
    >
      <Icon size={16} strokeWidth={1.5} />
      {label}
    </button>
  );
}

function Field({ label, children, hint }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <label className="text-xs uppercase tracking-[0.18em] text-muted-foreground">{label}</label>
        {hint && <span className="text-[11px] text-muted-foreground italic">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Parse a stored delivery_window string back into { start, end } as "YYYY-MM" values
// (for HTML <input type="month">). Accepts our formatted output as well as legacy
// free-text — anything unrecognized leaves both fields empty.
function parseDeliveryWindow(s) {
  if (!s) return { start: "", end: "" };
  const re = /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+(\d{4})/g;
  const hits = [];
  let m;
  while ((m = re.exec(s)) !== null) {
    const monthIdx = MONTHS_SHORT.indexOf(m[1]);
    if (monthIdx >= 0) hits.push(`${m[2]}-${String(monthIdx + 1).padStart(2, "0")}`);
  }
  return { start: hits[0] || "", end: hits[1] || "" };
}

function formatDeliveryWindow(start, end) {
  const fmt = (ym) => {
    if (!ym) return "";
    const [y, mo] = ym.split("-");
    const idx = parseInt(mo, 10) - 1;
    if (idx < 0 || idx > 11) return "";
    return `${MONTHS_SHORT[idx]} ${y}`;
  };
  const s = fmt(start);
  const e = fmt(end);
  if (s && e && s !== e) return `${s} – ${e}`;
  return s || e || "";
}

function DeliveryWindowRange({ value, onChange, disabled }) {
  const { start, end } = parseDeliveryWindow(value);
  const set = (next) => onChange(formatDeliveryWindow(next.start, next.end));
  return (
    <div className="flex items-center gap-2">
      <input
        data-testid="form-delivery-window-start"
        type="month"
        value={start}
        disabled={disabled}
        onChange={(e) => set({ start: e.target.value, end })}
        className="flex-1 px-4 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary disabled:opacity-40"
      />
      <span className="text-muted-foreground text-sm">to</span>
      <input
        data-testid="form-delivery-window-end"
        type="month"
        value={end}
        disabled={disabled}
        onChange={(e) => set({ start, end: e.target.value })}
        className="flex-1 px-4 py-3 rounded-full border border-border bg-transparent focus:outline-none focus:border-primary disabled:opacity-40"
      />
    </div>
  );
}

function Empty({ title, body, testid }) {
  return (
    <div data-testid={testid} className="text-center py-20 border border-dashed border-border rounded-2xl">
      <p className="font-serif text-2xl mb-2">{title}</p>
      <p className="text-muted-foreground text-sm">{body}</p>
    </div>
  );
}

